// src/components/WalletPage/WalletPage.jsx
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import styles from "../Home/HomePage.module.css";
const authenticatedFetch = (url, options = {}) => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (!token) {
    window.location.href = '/#/login';
    throw new Error('No auth token');
  }
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    },
  });
};

const API_BASE = "https://simplix.co.za/api";
//const API_BASE = "http://127.0.0.1:8000/api";
const WALLET_CACHE_TTL = 60 * 1000;

const walletCacheKey = (accountId) => `wallet_page_${accountId}`;

function getCachedWallet(accountId) {
  try {
    const cached = JSON.parse(
      sessionStorage.getItem(walletCacheKey(accountId)) || "null",
    );
    if (!cached || Date.now() - cached.ts > WALLET_CACHE_TTL) return null;
    return cached.data || null;
  } catch {
    return null;
  }
}

function setCachedWallet(accountId, data) {
  try {
    sessionStorage.setItem(
      walletCacheKey(accountId),
      JSON.stringify({ ts: Date.now(), data }),
    );
  } catch {}
}
// helpers
const currency = (n) =>
  "R" +
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function useAuthTokenEmail() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const profileEmail =
    (typeof window !== "undefined" && localStorage.getItem("userEmail")) ||
    (() => {
      try {
        const decoded = JSON.parse(atob(token.split(".")[1] || ""));
        return decoded?.email || "";
      } catch {
        return "";
      }
    })();
  return { token, profileEmail };
}

function useAccountId() {
  const { accountId: accountIdFromRoute } = useParams();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const accountIdFromQuery = qs.get("accountId");
  return accountIdFromRoute || accountIdFromQuery || "";
}

export default function WalletPage() {
  const [paymentMeterId, setPaymentMeterId] = useState("");
  const { token } = useAuthTokenEmail();
  const accountId = useAccountId();
  const [estimatedRemainingKwh, setEstimatedRemainingKwh] = useState(null);

  // filters
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [minAmt, setMinAmt] = useState("");
  const [maxAmt, setMaxAmt] = useState("");
  const [sort, setSort] = useState("date_asc"); // date_desc | date_asc | amount_desc | amount_asc

  // data
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]); // raw BC rows
  const [customerNumber, setCustomerNumber] = useState("");
  const [balance, setBalance] = useState(0);
  const [avgSpendPerDayR, setAvgSpendPerDayR] = useState(null);

  // paging
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // download info
  const [dlError, setDlError] = useState("");
  const [dlSuccess, setDlSuccess] = useState("");
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/"); // or "/wallet" or "/dashboard"
    }
  };

  const estimatedDaysLeft = useMemo(() => {
    const currentBalance = Number(balance || 0);

    // In BC: negative balance = customer is in credit/good.
    // Zero or positive balance = bad/no credit remaining.
    if (currentBalance >= 0) {
      return 0;
    }

    if (!avgSpendPerDayR || avgSpendPerDayR <= 0) {
      return Infinity;
    }

    return Math.abs(currentBalance) / avgSpendPerDayR;
  }, [balance, avgSpendPerDayR]);

  const estimatedDaysLeftFloor = useMemo(() => {
    return Number.isFinite(estimatedDaysLeft)
      ? Math.floor(estimatedDaysLeft)
      : null;
  }, [estimatedDaysLeft]);

  const needsAttention =
    Number(balance || 0) >= 0 ||
    (estimatedDaysLeftFloor != null && estimatedDaysLeftFloor <= 10);

  const humanizeDays = (days) => {
    if (!Number.isFinite(days)) return "—";
    if (days < 1) return `${Math.floor(days * 24)} hrs`;
    if (days < 14) return `${Math.floor(days)} days`;
    const weeks = days / 7;
    if (weeks < 8) return `${weeks.toFixed(1)} weeks`;
    return `${(days / 30.4375).toFixed(1)} months`;
  };

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function loadWallet() {
      if (!accountId) {
        setErr(
          "No wallet account was selected. Please return to your meters and try again.",
        );
        setLoading(false);
        return;
      }

      setLoading(true);
      setErr("");

      const cached = getCachedWallet(accountId);

      if (cached) {
        setRows(cached.entries || []);
        setCustomerNumber(cached.customerNumber || "");
        setBalance(cached.balance || 0);
        setPaymentMeterId(cached.paymentMeterId || "");
        setAvgSpendPerDayR(cached.avgSpendPerDayR || 0);
        setEstimatedRemainingKwh(cached.estimatedRemainingKwh ?? null);
      }

      try {
        const email = (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim();

        if (!email) {
          navigate("/#/login", { replace: true });
          return;
        }

        const ledgerPromise = authenticatedFetch(
          `${API_BASE}/bc/customer-ledger/?account_id=${encodeURIComponent(accountId)}`,
          {
            signal: controller.signal,
          },
        );

        const accountsPromise = cached
          ? null
          : authenticatedFetch(`${API_BASE}/accounts`, {
              headers: { Accept: "application/json" },
              signal: controller.signal,
            });

        const [ar, ledgerRes] = await Promise.all([
          accountsPromise,
          ledgerPromise,
        ]);

        if (ar && !ar.ok) {
  throw new Error("Account information could not be loaded");
}

        if (ar?.ok) {
          const aj = await ar.json();
          const accounts = Array.isArray(aj?.accounts) ? aj.accounts : [];

          const hasPending = accounts.some(
            (a) => String(a?.status || "").toLowerCase() !== "active",
          );

          if (hasPending) {
            sessionStorage.setItem(
              "profile_notice",
              "Please upload your ID document for your pending account before using meters, wallets or payments.",
            );
            navigate("/profile", { replace: true });
            return;
          }
        }

        const js = await ledgerRes.json();

        if (!ledgerRes.ok || !js?.ok) {
          throw new Error(js?.error || "BC returned an error");
        }

        if (cancelled) return;

        const entries = Array.isArray(js.entries) ? js.entries : [];
        const newBalance = Number(js.balance || 0);
        const newCustomerNumber = js.customerNumber || "";

        setRows(entries);
        setCustomerNumber(newCustomerNumber);
        setBalance(newBalance);

        const now = new Date();
        const start = new Date();
        start.setDate(now.getDate() - 29);

        let spendTotal = 0;
        let firstDebit = null;

        for (const row of entries) {
          const d = row.postingDate ? new Date(row.postingDate) : null;
          const amt = Number(row.amount || 0);

          if (d && d >= start && d <= now && amt > 0) {
            spendTotal += amt;
            if (!firstDebit || d < firstDebit) firstDebit = d;
          }
        }

        let avg = 0;

        if (spendTotal > 0 && firstDebit) {
          const actualDays =
            Math.floor((now.getTime() - firstDebit.getTime()) / 86400000) + 1;

          avg = spendTotal / Math.min(Math.max(actualDays, 1), 30);
        }

        setAvgSpendPerDayR(avg);

        let meterId = "";
        let newEstimatedRemainingKwh = null;

        try {
          const metersRes = await authenticatedFetch(
            `${API_BASE}/meters`,
            { signal: controller.signal },
          );

          const metersJson = await metersRes.json().catch(() => ({}));

          const linkedMeter = (metersJson?.meters || []).find(
            (m) => String(m.linkedAccountId) === String(accountId),
          );

          meterId = linkedMeter?.id || "";
          setPaymentMeterId(meterId);

          if (meterId) {
            const detailRes = await authenticatedFetch(`${API_BASE}/meters/${meterId}/`, {
              signal: controller.signal,
            });

            const detailJson = await detailRes.json().catch(() => ({}));

            const rate = Number(detailJson?.meter?.effectiveRateRPerKwh || 0);

            newEstimatedRemainingKwh =
              newBalance < 0 && rate > 0
                ? Math.max(0, Math.abs(newBalance) / rate)
                : 0;
          }
        } catch {
          newEstimatedRemainingKwh = null;
        }

        if (cancelled) return;

        setEstimatedRemainingKwh(newEstimatedRemainingKwh);
        setLoading(false);

        setCachedWallet(accountId, {
          entries,
          balance: newBalance,
          customerNumber: newCustomerNumber,
          paymentMeterId: meterId,
          avgSpendPerDayR: avg,
          estimatedRemainingKwh: newEstimatedRemainingKwh,
        });
      } catch (e) {
        if (!cancelled && e.name !== "AbortError") {
          console.error(e);
          setErr(e?.message || "Failed to load BC customer ledger.");
          setLoading(false);
        }
      }
    }

    loadWallet();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [accountId, token, navigate]);

  // apply filters + sort locally
  const filtered = useMemo(() => {
    let list = [...rows];

    if (q) {
      const needle = q.toLowerCase();
      list = list.filter(
        (r) =>
          (r.comment || "").toLowerCase().includes(needle) ||
          (r.documentNumber || "").toLowerCase().includes(needle) ||
          (r.externalDocumentNumber || "").toLowerCase().includes(needle),
      );
    }

    if (from) {
      const fromDate = new Date(from);
      list = list.filter((r) =>
        r.postingDate ? new Date(r.postingDate) >= fromDate : true,
      );
    }

    if (to) {
      const toDate = new Date(to);
      list = list.filter((r) =>
        r.postingDate ? new Date(r.postingDate) <= toDate : true,
      );
    }

    if (minAmt) {
      const min = Number(minAmt);
      list = list.filter((r) => Number(r.amount || 0) >= min);
    }

    if (maxAmt) {
      const max = Number(maxAmt);
      list = list.filter((r) => Number(r.amount || 0) <= max);
    }

    // Newest first, so the current balance sits on the latest transaction
    list.sort((a, b) => {
      const da = a.postingDate ? new Date(a.postingDate).getTime() : 0;
      const db = b.postingDate ? new Date(b.postingDate).getTime() : 0;

      if (db !== da) return db - da;

      return Number(b.entryNumber || 0) - Number(a.entryNumber || 0);
    });

    return list;
  }, [rows, q, from, to, minAmt, maxAmt]);

  const withUiRunningBalance = useMemo(() => {
    let running = Number(balance || 0);

    return filtered.map((r) => {
      const amt = Number(r.amount || 0);

      const row = {
        ...r,
        uiRunningBalance: running,
      };

      // Only move backwards for transactions included in the real balance.
      // Future-month transactions are displayed but must not affect balance.
       if (!r.notYetDue) {
        running -= amt;
      }

      return row;
    });
  }, [filtered, balance]);

  // page slice
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return withUiRunningBalance.slice(start, end);
  }, [withUiRunningBalance, page, pageSize]);

  // reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [q, from, to, minAmt, maxAmt, sort]);

  const resetFilters = () => {
    setQ("");
    setFrom("");
    setTo("");
    setMinAmt("");
    setMaxAmt("");
    setSort("date_desc");
  };

  // download statement as Excel
  const handleDownloadStatement = async () => {
    setDlError("");
    setDlSuccess("");

    try {
      // Build rows for Excel
      const data = filtered.map((r) => ({
        "Posting Date": r.postingDate
          ? format(new Date(r.postingDate), "yyyy-MM-dd")
          : "",
        "Document Number": r.documentNumber || "",
        Amount: Number(r.amount || 0),
      }));

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);

      // Make columns look nice
      worksheet["!cols"] = [
        { wch: 14 }, // Posting Date
        { wch: 16 }, // Document Type
        { wch: 20 }, // Document Number
        { wch: 26 }, // External Document Number
        { wch: 14 }, // Amount
        { wch: 18 }, // Running Balance
      ];

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Ledger");

      // Filename
      const niceFrom = from || "begin";
      const niceTo = to || "today";
      const filename = `bc-ledger_${customerNumber || accountId}.xlsx`;

      // Trigger download
      XLSX.writeFile(workbook, filename);

      setDlSuccess("Statement downloaded as Excel.");
    } catch (e) {
      setDlError(e?.message || "Failed to download statement.");
    }
  };

  const downloadRowPdf = (r) => {
    const doc = new jsPDF();

    doc.setFontSize(14);
    doc.text("Ledger Entry Details", 14, 20);

    doc.setFontSize(10);
    const lines = [r.comment ? `Details: ${r.comment}` : ""].filter(Boolean);

    let y = 35;
    lines.forEach((l) => {
      doc.text(l, 14, y);
      y += 8;
    });

    doc.save(
      `ledger-entry-${r.documentNumber || r.entryNumber || "entry"}.pdf`,
    );
  };

  if (err) {
  return (
    <div className={styles.paymentStateScreen}>
      <div className={styles.paymentStateCard}>
        <div className={styles.paymentStateIcon}>!</div>
        <h2>Wallet unavailable</h2>
        <p>
          We could not load your wallet information. Please refresh the page
          and try again.
        </p>

        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => window.location.reload()}
        >
          Refresh and retry
        </button>

        <button
          type="button"
          className={styles.paymentStateBack}
          onClick={handleBack}
        >
          Go back
        </button>
      </div>
    </div>
  );
}

if (loading) {
  return (
    <div className={styles.paymentStateScreen}>
      <div className={styles.paymentStateCard}>
        <div className={styles.paymentStateSpinner} />
        <h2>Loading wallet</h2>
        <p>
          We are retrieving your latest balance, transactions and meter
          information.
        </p>
        <strong>Please wait.</strong>
      </div>
    </div>
  );
}

  return (
    <div className={styles.page}>
      <div className={`${styles.section} ${styles.meterDetailSection}`}>
        <div className={styles.meterDetailHero}>
          <div>
            <p className={styles.sectionEyebrow}>Wallet</p>
            <h1 className={styles.h2}>
              Customer Ledger
              {customerNumber && (
                <span className={styles.micro} style={{ marginLeft: 8 }}>
                  {customerNumber}
                </span>
              )}
            </h1>
          </div>

          <div className={styles.meterDetailActions}>
            <button className={styles.inlineBtn} onClick={handleBack}>
              ← Back
            </button>

            {customerNumber && paymentMeterId && (
              <button
                className={styles.primaryBtn}
                onClick={() =>
                  navigate(
                    `/payment?meter=${encodeURIComponent(paymentMeterId)}`,
                  )
                }
              >
                Fund Account
              </button>
            )}

            <button
              className={styles.secondaryBtn}
              onClick={handleDownloadStatement}
            >
              Download Excel
            </button>
          </div>
        </div>

        <div className={styles.meterDetailPanel}>
          <div
            className={styles.meterDetailGrid}
            style={{
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            }}
          >
            <div
              className={
                Number(balance) < 0
                  ? styles.walletPositiveCard
                  : Number(balance) > 0
                    ? styles.walletNegativeCard
                    : styles.meterDetailCard
              }
            >
              <h3>
                {Number(balance) < 0
                  ? "Current Balance"
                  : Number(balance) > 0
                    ? "Outstanding Balance"
                    : "Current Balance"}
              </h3>

              <p>
                {currency(
                  Number(balance) < 0
                    ? Math.abs(Number(balance))
                    : Number(balance),
                )}
              </p>

              <span>
                {Number(balance) < 0
                  ? "Account is in credit"
                  : Number(balance) > 0
                    ? "Account is overdue"
                    : "Account is settled"}
              </span>
            </div>

            <div
              className={
                needsAttention
                  ? styles.walletWarningCard
                  : styles.meterDetailCard
              }
            >
              <h3>Estimated days remaining</h3>
              <p>
                {estimatedDaysLeftFloor != null ? estimatedDaysLeftFloor : "—"}
              </p>
              <span>{humanizeDays(estimatedDaysLeft)}</span>
            </div>

            <div className={styles.meterDetailCard}>
              <h3>Estimated kWh remaining</h3>

              <p>
                {estimatedRemainingKwh != null
                  ? estimatedRemainingKwh.toFixed(1)
                  : "—"}
              </p>

              <span>
                {estimatedRemainingKwh != null
                  ? "Estimated remaining prepaid units"
                  : "Could not calculate"}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.meterDetailPanel}>
          <div className={styles.meterDetailPanelHeader}>
            <div>
              <p className={styles.sectionEyebrow}>Filters</p>
              <h2>Ledger filters</h2>
            </div>
          </div>

          <div className={styles.walletFiltersGrid}>
            <input
              className={styles.authInput}
              placeholder="Search..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />

            <input
              className={styles.authInput}
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />

            <input
              className={styles.authInput}
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />

            <input
              className={styles.authInput}
              type="number"
              placeholder="Min"
              value={minAmt}
              onChange={(e) => setMinAmt(e.target.value)}
            />

            <input
              className={styles.authInput}
              type="number"
              placeholder="Max"
              value={maxAmt}
              onChange={(e) => setMaxAmt(e.target.value)}
            />

            <button className={styles.inlineBtn} onClick={resetFilters}>
              Reset
            </button>
          </div>

          {(dlError || dlSuccess) && (
            <div style={{ marginTop: 14 }}>
              {dlError && <div className={styles.authError}>{dlError}</div>}
              {dlSuccess && (
                <div className={styles.authSuccess}>{dlSuccess}</div>
              )}
            </div>
          )}
        </div>

        <div className={styles.meterDetailPanel}>
          <div className={styles.meterDetailPanelHeader}>
            <div>
              <p className={styles.sectionEyebrow}>Ledger</p>
              <h2>Ledger entries</h2>
              <p className={styles.meterPanelText}>
                {loading ? "Loading…" : `${filtered.length} entries found`}
              </p>
            </div>
          </div>

          <div className={styles.meterReadingsTableCard}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Document</th>
                  <th>Description</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "right" }}>Balance</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {!loading &&
                  paged.map((r) => {
                    const d = r.postingDate
                      ? format(new Date(r.postingDate), "yyyy-MM-dd")
                      : "—";

                    const isNotYetDue = r.notYetDue === true;

                    return (
                      <tr key={r.entryNumber}>
                        <td>
                          {d}
                          {isNotYetDue && (
                            <div
                              style={{
                                marginTop: 6,
                                fontSize: 11,
                                fontWeight: 900,
                                color: "#9a3412",
                              }}
                            >
                              Not yet due
                            </div>
                          )}
                        </td>

                        <td>
                          {r.documentNumber && (
                            <span className={styles.badge}>
                              #{r.documentNumber}
                            </span>
                          )}
                        </td>

                        <td>{r.externalDocumentNumber || "—"}</td>

                        <td
                          style={{
                            textAlign: "right",
                            fontWeight: 900,
                            color:
                              Number(r.amount || 0) < 0 ? "#139619" : "#bb2323",
                          }}
                        >
                          {currency(r.amount || 0)}
                        </td>

                        <td style={{ textAlign: "right", fontWeight: 900 }}>
                          {isNotYetDue ? (
                            <>
                              <span style={{ color: "#9a3412" }}>
                                Not included yet
                              </span>
                              <br />
                            </>
                          ) : r.uiRunningBalance != null ? (
                            currency(r.uiRunningBalance)
                          ) : (
                            "—"
                          )}
                        </td>

                        <td>
                          <button
                            className={styles.inlineBtn}
                            onClick={() => downloadRowPdf(r)}
                          >
                            PDF
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                {!loading && paged.length === 0 && (
                  <tr>
                    <td colSpan="6">No entries found.</td>
                  </tr>
                )}

              </tbody>
            </table>
          </div>

          <div className={styles.walletPagination}>
            <div className={styles.meterPanelText}>
              Page {page} of {pages}
            </div>

            <div className={styles.meterChartActions}>
              <select
                className={styles.authInput}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n}>{n}</option>
                ))}
              </select>

              <button
                className={styles.inlineBtn}
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Prev
              </button>

              <button
                className={styles.primaryBtn}
                disabled={page === pages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
